/**
 * Plazo de la sesión y su MOTIVO (función pura, sin efectos).
 *
 *   idleDeadline     = última actividad + policy.idleMs
 *   absoluteDeadline = login real (last_sign_in_at) + policy.maxMs
 *
 * Gana el plazo más próximo, y el motivo importa para la UX: un aviso por
 * inactividad se resuelve con "Mantener sesión"; uno por duración máxima no
 * (ese botón no puede extender el tope absoluto). En empate gana 'absolute',
 * porque reiniciar la inactividad no movería el cierre.
 */
import type { SessionPolicy } from './sessionPolicy';

export type SessionDeadlineKind = 'idle' | 'absolute';

export interface SessionDeadline {
  kind: SessionDeadlineKind;
  /** ms hasta el cierre (≤ 0 = ya venció). */
  remainingMs: number;
}

/**
 * Actividad recibida de OTRA pestaña (evento `storage` de `lc_last_activity`).
 * Solo se adopta un timestamp válido, posterior a la actividad que esta pestaña
 * ya conoce, no futuro y no anterior al login actual; en cualquier otro caso
 * → null (se ignora). Sin `loginAt` resuelto no se adopta nada.
 * Solo mueve la inactividad: el tope absoluto sale de `loginAt`, que esto no toca.
 */
export function acceptSharedActivity(
  raw: string | null,
  ctx: { known: number; now: number; loginAt: number | null }
): number | null {
  if (raw === null || ctx.loginAt === null) return null;
  const ts = Number(raw);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  if (ts <= ctx.known) return null; // no es más reciente
  if (ts > ctx.now) return null; // futuro: no extiende la sesión
  if (ts < ctx.loginAt) return null; // de antes del login actual
  return ts;
}

export function computeSessionDeadline(input: {
  now: number;
  lastActivity: number;
  loginAt: number;
  policy: Pick<SessionPolicy, 'idleMs' | 'maxMs'>;
}): SessionDeadline {
  const idleDeadline = input.lastActivity + input.policy.idleMs;
  const absoluteDeadline = input.loginAt + input.policy.maxMs;
  if (absoluteDeadline <= idleDeadline) {
    return { kind: 'absolute', remainingMs: absoluteDeadline - input.now };
  }
  return { kind: 'idle', remainingMs: idleDeadline - input.now };
}
